import { DateTime } from 'luxon';
import { getPeriodStatus } from '@/helpers/periodStatus';
import type { CollectionTicketClaimDTO } from '@/api/trafikportalen';

export type PeriodConfiguration = Pick<
  CollectionTicketClaimDTO,
  | 'valid_from'
  | 'valid_to'
  | 'type'
  | 'revoke_strategy'
  | 'reissuance_allowed'
  | 'relational'
  | 'stina_product_id'
> & {
  non_invoiced?: boolean;
};

const normalizeInstant = (value?: string): string => {
  if (!value) return '';
  const parsed = DateTime.fromISO(value, { setZone: true });
  if (!parsed.isValid) return value;
  return parsed.toUTC().toISO({ suppressMilliseconds: true }) ?? value;
};

/**
 * Periods match when the validity window, the ticket settings, and whether
 * tickets are billed are the same. Timestamps are compared as instants so
 * equivalent offsets still match.
 */
export const periodConfigurationKey = (claim: PeriodConfiguration): string =>
  JSON.stringify([
    normalizeInstant(claim.valid_from),
    normalizeInstant(claim.valid_to),
    claim.type,
    claim.revoke_strategy,
    claim.reissuance_allowed,
    claim.relational,
    claim.stina_product_id ?? '',
    claim.non_invoiced === true,
  ]);

export const findClaimsWithSameConfiguration = (
  claims: CollectionTicketClaimDTO[],
  template: PeriodConfiguration,
): CollectionTicketClaimDTO[] => {
  const key = periodConfigurationKey(template);
  return claims.filter((claim) => periodConfigurationKey(claim) === key);
};

/** Selected organisations that already have this exact period. */
export const organizationIdsWithSamePeriod = (
  claims: CollectionTicketClaimDTO[] | undefined,
  organizationIds: Iterable<string>,
  configuration: PeriodConfiguration,
): string[] => {
  const selected = new Set(organizationIds);
  const key = periodConfigurationKey(configuration);
  const matched = new Set<string>();
  for (const claim of claims ?? []) {
    if (!selected.has(claim.organization_id)) continue;
    if (periodConfigurationKey(claim) === key) {
      matched.add(claim.organization_id);
    }
  }
  return [...matched];
};

/**
 * A selection narrows the edit only when it includes some, but not all,
 * organisations that use the period. No matching selection leaves every
 * organisation on the period.
 */
export const claimsForPeriodEndEdit = (
  claims: CollectionTicketClaimDTO[],
  selectedOrganizationIds: ReadonlySet<string>,
): CollectionTicketClaimDTO[] => {
  const periodOrganizationIds = new Set(
    claims.map((claim) => claim.organization_id),
  );
  const selectedInPeriod = [...periodOrganizationIds].filter((id) =>
    selectedOrganizationIds.has(id),
  );
  const onlySelected =
    selectedInPeriod.length > 0 &&
    selectedInPeriod.length < periodOrganizationIds.size;
  if (!onlySelected) return claims;

  const selected = new Set(selectedInPeriod);
  return claims.filter((claim) => selected.has(claim.organization_id));
};

export interface PeriodConfigurationGroup {
  key: string;
  claims: CollectionTicketClaimDTO[];
  organizationIds: string[];
}

/** Active and upcoming periods that share validity and ticket settings. */
export const groupCurrentPeriods = (
  claims: CollectionTicketClaimDTO[],
): PeriodConfigurationGroup[] => {
  const groups = new Map<string, CollectionTicketClaimDTO[]>();
  for (const claim of claims) {
    if (getPeriodStatus(claim) === 'past') continue;
    const key = periodConfigurationKey(claim);
    const existing = groups.get(key);
    if (existing) existing.push(claim);
    else groups.set(key, [claim]);
  }

  return [...groups.entries()]
    .map(([key, groupClaims]) => ({
      key,
      claims: groupClaims,
      organizationIds: [
        ...new Set(groupClaims.map((claim) => claim.organization_id)),
      ],
    }))
    .sort((a, b) =>
      (a.claims[0]?.valid_from ?? '').localeCompare(
        b.claims[0]?.valid_from ?? '',
      ),
    );
};
